import { lazy } from "react";
import type { RouteConfig } from "../config";
import { ROUTES } from "../paths";

const ProductionMapPage = lazy(() =>
  import("@/pages/production/production-map-page").then((m) => ({
    default: m.ProductionMapPage,
  }))
);
const CreateForStockPage = lazy(() =>
  import("@/pages/factory-orders/create-for-stock-page").then((m) => ({
    default: m.CreateForStockPage,
  }))
);
const FactoryOrdersListPage = lazy(() =>
  import("@/pages/factory-orders/factory-orders-list-page").then((m) => ({
    default: m.FactoryOrdersListPage,
  }))
);
const FactoryOrderDetailPage = lazy(() =>
  import("@/pages/factory-orders/factory-order-detail-page").then((m) => ({
    default: m.FactoryOrderDetailPage,
  }))
);
const MaterialOrdersPage = lazy(() =>
  import("@/pages/material-orders/material-orders-page").then((m) => ({
    default: m.MaterialOrdersPage,
  }))
);
const MaintenancePage = lazy(() =>
  import("@/pages/maintenance/maintenance-page").then((m) => ({
    default: m.MaintenancePage,
  }))
);
const PlanningPage = lazy(() =>
  import("@/pages/planning/planning-page").then((m) => ({
    default: m.PlanningPage,
  }))
);
const SubcontractingPage = lazy(() =>
  import("@/pages/subcontracting/subcontracting-page").then((m) => ({
    default: m.SubcontractingPage,
  }))
);
const QualityPage = lazy(() =>
  import("@/pages/quality/quality-page").then((m) => ({
    default: m.QualityPage,
  }))
);
const ProductionLotsPage = lazy(() =>
  import("@/pages/production-lots/production-lots-page").then((m) => ({
    default: m.ProductionLotsPage,
  }))
);
const ExpeditionsListPage = lazy(() =>
  import("@/pages/expeditions/expeditions-list-page").then((m) => ({
    default: m.ExpeditionsListPage,
  }))
);
const ShipmentDetailPage = lazy(() =>
  import("@/pages/clients/expeditions/shipment-detail-page").then((m) => ({
    default: m.ShipmentDetailPage,
  }))
);

const ProceduresListPage = lazy(() =>
  import("@/pages/producao/procedures/procedures-list-page").then((m) => ({
    default: m.ProceduresListPage,
  }))
);
const ProcedureEditorPage = lazy(() =>
  import("@/pages/producao/procedures/procedure-editor-page").then((m) => ({
    default: m.ProcedureEditorPage,
  }))
);
const MyProceduresPage = lazy(() =>
  import("@/pages/producao/procedures/my-procedures-page").then((m) => ({
    default: m.MyProceduresPage,
  }))
);

const PRODUCTION_MAP_READ = { access: "permission" as const, entity: "productions", action: "READ" };
const ORDER_READ = { access: "permission" as const, entity: "orders", action: "READ" };
// Entidade e feature têm de bater com o gate do backend (`require_feature("ordens_fabrico.planeamento")`).
const PLANNING_READ = { access: "permission" as const, entity: "production_tasks", action: "READ", feature: "ordens_fabrico.planeamento" as const };
// Idem: entidade e feature batem com `require_feature("ordens_fabrico.lotes")` do servidor.
// Idem para a revista: `require_feature("ordens_fabrico.qualidade")` e a entidade `production_quality`.
const QUALITY_READ = { access: "permission" as const, entity: "production_quality", action: "READ", feature: "ordens_fabrico.qualidade" as const };
const PRODUCTION_LOTS_READ = { access: "permission" as const, entity: "production_lots", action: "READ", feature: "ordens_fabrico.lotes" as const };
// Idem para a subcontratação: `require_feature("ordens_fabrico.subcontratacao")` e a entidade `subcontract_orders`.
const SUBCONTRACTING_READ = { access: "permission" as const, entity: "subcontract_orders", action: "READ", feature: "ordens_fabrico.subcontratacao" as const };
// Manutenção e encomendas de material vendem-se à parte do módulo: `require_feature` do servidor e `ENTITY_FEATURES`.
const MAINTENANCE_READ = { access: "permission" as const, entity: "maintenance", action: "READ", feature: "producao.manutencao" as const };
const MATERIAL_ORDERS_READ = { access: "permission" as const, entity: "material_orders", action: "READ", feature: "producao.aprovisionamento" as const };
const PROCEDURES_READ = { access: "permission" as const, entity: "procedures", action: "READ" };
// `factory_orders` é entidade própria desde 2026-09-04 (domínio
// `ordens_fabrico`) — não `orders`, que é só `CustomerOrder` e vive sob o
// módulo `comercial`. Ver `app/api/routes/orders/factory_orders.py`.
const FACTORY_ORDER_READ = { access: "permission" as const, entity: "factory_orders", action: "READ" };
const FACTORY_ORDER_CREATE = { access: "permission" as const, entity: "factory_orders", action: "CREATE" };

export const productionRoutes: RouteConfig[] = [
  { path: ROUTES.PROCEDURES.LIST, component: ProceduresListPage, layout: "dashboard", permissions: PROCEDURES_READ },
  { path: ROUTES.PROCEDURES.EDITOR, component: ProcedureEditorPage, layout: "dashboard", permissions: PROCEDURES_READ },
  { path: ROUTES.PROCEDURES.MINE, component: MyProceduresPage, layout: "dashboard", permissions: PROCEDURES_READ },
  { path: ROUTES.PRODUCTIONS.MAP, component: ProductionMapPage, layout: "dashboard", permissions: PRODUCTION_MAP_READ },
  { path: ROUTES.FACTORY_ORDERS.CREATE_FOR_STOCK, component: CreateForStockPage, layout: "dashboard", permissions: FACTORY_ORDER_CREATE },
  { path: ROUTES.FACTORY_ORDERS.LIST, component: FactoryOrdersListPage, layout: "dashboard", permissions: FACTORY_ORDER_READ },
  { path: ROUTES.FACTORY_ORDERS.DETAIL, component: FactoryOrderDetailPage, layout: "dashboard", permissions: FACTORY_ORDER_READ },
  { path: ROUTES.MATERIAL_ORDERS.LIST, component: MaterialOrdersPage, layout: "dashboard", permissions: MATERIAL_ORDERS_READ },
  { path: ROUTES.MAINTENANCE.LIST, component: MaintenancePage, layout: "dashboard", permissions: MAINTENANCE_READ },
  { path: ROUTES.PLANNING.LIST, component: PlanningPage, layout: "dashboard", permissions: PLANNING_READ },
  { path: ROUTES.PRODUCTION_LOTS.LIST, component: ProductionLotsPage, layout: "dashboard", permissions: PRODUCTION_LOTS_READ },
  { path: ROUTES.QUALITY.LIST, component: QualityPage, layout: "dashboard", permissions: QUALITY_READ },
  { path: ROUTES.SUBCONTRACTING.LIST, component: SubcontractingPage, layout: "dashboard", permissions: SUBCONTRACTING_READ },
  { path: ROUTES.EXPEDITIONS.LIST, component: ExpeditionsListPage, layout: "dashboard", permissions: ORDER_READ },
  { path: ROUTES.EXPEDITIONS.DETAIL, component: ShipmentDetailPage, layout: "dashboard", permissions: ORDER_READ },
];
